import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ScheduleSettingsUI } from "@/components/settings/schedule-settings";
import {
  MentorSchedule,
  MentorScheduleItem,
  ScheduleSchema,
  useGetAllMentorSchedules,
  useUpdateUserSchedule,
} from "@/lib/api/user";
import { DEFAULT_TIMEZONE } from "@/lib/constants";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { getInitials } from "@/lib/utils";
import { requirePermission } from "@/lib/casl/route-guards";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Clock, Edit2, Loader2 } from "lucide-react";
import { useState, useMemo } from "react";

export const Route = createFileRoute("/_authenticated/mentors/schedules")({
  component: MentorSchedules,
  beforeLoad: ({ context }) => {
    requirePermission(context.user, "read", "mentor-slots");
  },
});

const daysOfWeek = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const timezoneAbbreviations: Record<string, string> = {
  [DEFAULT_TIMEZONE]: "ET",
  "America/Chicago": "CT",
  "America/Denver": "MT",
  "America/Los_Angeles": "PT",
  "America/Anchorage": "AKT",
  "America/Adak": "HST",
  "Pacific/Honolulu": "HST",
};

function getTimezoneAbbreviation(timezone: string): string {
  return timezoneAbbreviations[timezone] || timezone;
}

function formatScheduleSummary(schedule: MentorScheduleItem[]): string {
  if (!schedule || schedule.length === 0) {
    return "No schedule set";
  }

  const onlineDays = schedule.filter((s) => s.isOnline);
  if (onlineDays.length === 0) {
    return "All days offline";
  }

  const dayGroups: { days: string[]; time: string }[] = [];

  for (const day of onlineDays) {
    const timeRange = `${day.startTime}-${day.endTime}`;
    const existingGroup = dayGroups.find((g) => g.time === timeRange);
    if (existingGroup) {
      existingGroup.days.push(day.day);
    } else {
      dayGroups.push({ days: [day.day], time: timeRange });
    }
  }

  return dayGroups
    .map((group) => {
      const dayAbbrevs = group.days.map((d) => d.substring(0, 3));
      if (dayAbbrevs.length === 7) {
        return `Every day ${group.time}`;
      }
      if (dayAbbrevs.length >= 3) {
        const consecutive = areConsecutiveDays(group.days);
        if (consecutive) {
          return `${dayAbbrevs[0]}-${dayAbbrevs[dayAbbrevs.length - 1]} ${group.time}`;
        }
      }
      return `${dayAbbrevs.join(", ")} ${group.time}`;
    })
    .join("; ");
}

function areConsecutiveDays(days: string[]): boolean {
  const dayIndices = days
    .map((d) => daysOfWeek.indexOf(d))
    .sort((a, b) => a - b);
  for (let i = 1; i < dayIndices.length; i++) {
    if (dayIndices[i] - dayIndices[i - 1] !== 1) {
      return false;
    }
  }
  return true;
}

function MentorSchedules() {
  const { data: mentorSchedules, isPending } = useGetAllMentorSchedules();
  const [editingMentor, setEditingMentor] = useState<MentorSchedule | null>(
    null,
  );

  if (isPending) {
    return (
      <div className="flex h-[500px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-10">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Mentor Schedules</h1>
          <p className="mt-1 text-sm text-gray-600">
            View and manage mentor availability schedules. Times are shown in
            each mentor&apos;s local timezone.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/mentors">
            <Button variant="outline">View Slots</Button>
          </Link>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Mentor</TableHead>
            <TableHead>Timezone</TableHead>
            <TableHead>Schedule Summary</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {mentorSchedules?.map((mentorSchedule) => (
            <MentorScheduleRow
              key={mentorSchedule.mentor.id}
              mentorSchedule={mentorSchedule}
              onEdit={() => setEditingMentor(mentorSchedule)}
            />
          ))}
        </TableBody>
      </Table>

      {editingMentor && (
        <EditScheduleDialog
          mentorSchedule={editingMentor}
          onClose={() => setEditingMentor(null)}
        />
      )}
    </div>
  );
}

function MentorScheduleRow({
  mentorSchedule,
  onEdit,
}: {
  mentorSchedule: MentorSchedule;
  onEdit: () => void;
}) {
  const avatarUrl = useSignedMedia(mentorSchedule.mentor.profile_picture_url);
  const scheduleSummary = useMemo(
    () => formatScheduleSummary(mentorSchedule.schedule),
    [mentorSchedule.schedule],
  );
  const timezoneAbbrev = getTimezoneAbbreviation(mentorSchedule.timezone);

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          <Avatar>
            <AvatarImage src={avatarUrl?.url ?? ""} />
            <AvatarFallback>
              {getInitials(mentorSchedule.mentor.name)}
            </AvatarFallback>
          </Avatar>
          <span>{mentorSchedule.mentor.name}</span>
        </div>
      </TableCell>
      <TableCell>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4 text-gray-500" />
                <span className="font-medium">{timezoneAbbrev}</span>
                {mentorSchedule.auto_detect_timezone && (
                  <span className="text-xs text-gray-400">(auto)</span>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{mentorSchedule.timezone}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      <TableCell>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help text-sm">{scheduleSummary}</span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <ScheduleDetails schedule={mentorSchedule.schedule} />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      <TableCell>
        <Button onClick={onEdit} variant="outline" size="sm">
          <Edit2 className="mr-1 h-4 w-4" />
          Edit
        </Button>
      </TableCell>
    </TableRow>
  );
}

function ScheduleDetails({ schedule }: { schedule: MentorScheduleItem[] }) {
  if (!schedule || schedule.length === 0) {
    return <p className="text-sm text-gray-500">No schedule configured</p>;
  }

  return (
    <div className="space-y-1">
      {daysOfWeek.map((day) => {
        const daySchedule = schedule.find((s) => s.day === day);
        const isOnline = daySchedule?.isOnline ?? false;
        return (
          <div
            key={day}
            className="flex items-center justify-between gap-4 text-sm"
          >
            <span className="w-20">{day.substring(0, 3)}</span>
            {isOnline && daySchedule ? (
              <span className="text-green-600">
                {daySchedule.startTime} - {daySchedule.endTime}
              </span>
            ) : (
              <span className="text-gray-400">Offline</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function EditScheduleDialog({
  mentorSchedule,
  onClose,
}: {
  mentorSchedule: MentorSchedule;
  onClose: () => void;
}) {
  const { mutateAsync: updateScheduleMutation, isPending } =
    useUpdateUserSchedule(mentorSchedule.mentor.id);

  const defaultValues: ScheduleSchema = {
    schedule:
      mentorSchedule.schedule.length > 0
        ? mentorSchedule.schedule
        : daysOfWeek.map((day) => {
            if (
              day === "Monday" ||
              day === "Tuesday" ||
              day === "Wednesday" ||
              day === "Thursday"
            ) {
              return {
                day,
                isOnline: true,
                startTime: "17:00",
                endTime: "22:00",
              };
            } else if (day === "Friday") {
              return {
                day,
                isOnline: true,
                startTime: "17:00",
                endTime: "19:00",
              };
            } else {
              return {
                day,
                isOnline: false,
                startTime: "17:00",
                endTime: "19:00",
              };
            }
          }),
    timezone: mentorSchedule.timezone || DEFAULT_TIMEZONE,
    autoDetectTimezone: mentorSchedule.auto_detect_timezone ?? true,
  };

  const handleSubmit = async (data: ScheduleSchema) => {
    await updateScheduleMutation(data);
    onClose();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Edit Schedule for {mentorSchedule.mentor.name}
          </DialogTitle>
        </DialogHeader>
        <ScheduleSettingsUI
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          isPending={isPending}
          userName={mentorSchedule.mentor.name}
        />
      </DialogContent>
    </Dialog>
  );
}
